import React from 'react';
import {
  StyleSheet, Document, Page, View, Text
} from '@react-pdf/renderer';
import { toBrazilDateTime } from '../../../components/mask';

interface Row {
  Data: string;
  Cliente: string;
  Destinos: string;
  Condutor: string;
  Telefone: string;
  Status: string;
  'Data Criação': string;
}

interface OwnProps {
  data: Row[];
  fileName: string;
  filters: {
    subsidiary: string;
    status: string;
    period: string;
  };
  totalRecords: number;
}

export default function DeliveriesPrint({
  data,
  fileName,
  filters,
  totalRecords
}: OwnProps) {
  const styles = StyleSheet.create({
    body: {
      padding: 10,
      paddingBottom: 60
    },
    table: {
      width: 'auto',
      borderStyle: 'solid',
      borderColor: '#bfbfbf',
      borderWidth: 1,
      borderRightWidth: 0,
      borderBottomWidth: 0
    },
    tableRow: {
      margin: 'auto',
      flexDirection: 'row'
    },
    // Larguras proporcionais para as colunas
    colHeader: {
      width: '14%',
      borderStyle: 'solid',
      borderColor: '#bfbfbf',
      borderBottomColor: '#000',
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    col: {
      width: '14%',
      borderStyle: 'solid',
      borderColor: '#bfbfbf',
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    // Colunas maiores para campos de texto longo
    colLargeHeader: {
      width: '20%',
      borderStyle: 'solid',
      borderColor: '#bfbfbf',
      borderBottomColor: '#000',
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    colLarge: {
      width: '20%',
      borderStyle: 'solid',
      borderColor: '#bfbfbf',
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableCellHeader: {
      margin: 'auto',
      fontSize: 10,
      fontWeight: 500,
      textAlign: 'center'
    },
    tableCell: {
      fontSize: 8,
      padding: 2
    },
    // Estilo para o total
    totalCell: {
      fontSize: 9,
      padding: 4,
      fontWeight: 'bold'
    },
    header: {
      fontSize: 12,
      marginBottom: 10,
      textAlign: 'center',
      color: 'grey'
    },
    filters: {
      fontSize: 10,
      marginBottom: 15,
      textAlign: 'center',
      color: 'darkgrey'
    },
    total: {
      fontSize: 10,
      marginTop: 10,
      textAlign: 'right',
      fontWeight: 'bold'
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 10,
      bottom: 30,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: 'grey'
    }
  });

  const firstRow = data[0] || {};
  const rowKeys = Object.keys(firstRow);

  // Função para determinar a largura da coluna baseada no conteúdo
  const getColumnStyle = (key: string, colIndex: number) => {
    // Campos que precisam de mais espaço
    const largeColumns = ['Cliente', 'Destinos', 'Condutor'];
    if (largeColumns.includes(key)) {
      return colIndex === 0 ? styles.colLargeHeader : styles.colLarge;
    }
    return colIndex === 0 ? styles.colHeader : styles.col;
  };

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <Text style={styles.header} fixed>
          Relatório de Entregas
          {'\n'}
          {fileName}
          {'\n'}
          {toBrazilDateTime(new Date())}
        </Text>

        <Text style={styles.filters} fixed>
          Filtros Aplicados:
          {'\n'}
          Franquia:
          {' '}
          {filters.subsidiary}
          {'\n'}
          Status:
          {' '}
          {filters.status}
          {'\n'}
          Período:
          {' '}
          {filters.period}
        </Text>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            {rowKeys.map((key) => (
              <View style={getColumnStyle(key, 0)} key={`header-${key}`}>
                <Text style={styles.tableCellHeader}>{key}</Text>
              </View>
            ))}
          </View>
          {data.map((row) => (
            <View
              style={styles.tableRow}
              key={`row-${row.Data}-${row.Cliente}-${row.Condutor}`}
              wrap
            >
              {Object.entries(row).map(([key, value]) => (
                <View
                  style={getColumnStyle(key, 1)}
                  key={`cell-${row.Data}-${row.Cliente}-${key}`}
                >
                  <Text style={styles.tableCell}>{String(value)}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* TOTAL DE REGISTROS NO PDF */}
        <Text style={styles.total} fixed>
          {`TOTAL: ${totalRecords} registro${totalRecords !== 1 ? 's' : ''}`}
        </Text>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => (
            `${pageNumber} / ${totalPages}`
          )}
          fixed
        />
      </Page>
    </Document>
  );
}
